import type { Router } from 'vue-router'

import { isUserLoggedIn } from '@/router/utils'

import { xsrfToken } from './trackEvent'

/**
 * Records a logged-in arrival from a HubSpot product-update email (VH-2935).
 *
 * HubSpot adds `utm_source=hs_email` to marketing-email links, and whoever sends the
 * update adds `utm_campaign=<slug>` by hand. Only the slug is sent: the other HubSpot
 * parameters (`_hsenc` identifies the contact) never leave the browser.
 *
 * Runs on every completed navigation, not once at boot, because password and 2FA
 * login end with an in-app navigation to the original link rather than a page load.
 *
 * Uses `fetch` like `trackEvent`, not the shared axios client, for the same reasons:
 * axios cancels in-flight requests on navigation, and this fires on navigation.
 */

const ENDPOINT = `${import.meta.env.VITE_SERVER_URL}/api/email-campaign-arrivals`

/** Same pattern the server and its CHECK constraint enforce. */
const CAMPAIGN_PATTERN = /^[a-z0-9-]{1,64}$/

const STORAGE_PREFIX = 'vh_campaign_arrival:'

let installed = false

/** Whether this tab has already reported the campaign. Storage failures read as "no". */
function alreadySent(campaign: string): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + campaign) !== null
  } catch {
    return false
  }
}

function markSent(campaign: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + campaign, '1')
  } catch {
    // The server keeps the first arrival per user and campaign, so a repeat is harmless.
  }
}

function send(campaign: string, providerUuid: string | null): void {
  const token = xsrfToken()

  fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'X-XSRF-TOKEN': token } : {}),
      ...(providerUuid ? { 'X-Provider-UUID': providerUuid } : {}),
    },
    body: JSON.stringify({ campaign }),
  }).catch(() => {
    // A lost arrival is acceptable; nothing here may reach the user or Rollbar.
  })
}

export function installCampaignArrivalTracking(router: Router): void {
  if (installed)
    return

  installed = true

  router.afterEach((to, _from, failure) => {
    if (failure)
      return

    const { utm_source: source, utm_campaign: campaign } = to.query
    if (source !== 'hs_email' || typeof campaign !== 'string' || !CAMPAIGN_PATTERN.test(campaign))
      return

    // Logged out, the guard sends the user to login with this URL in `?to=`; the
    // navigation back to it after login lands here again.
    if (!isUserLoggedIn() || alreadySent(campaign))
      return

    // A go link waiting at the hospital picker: the arrival belongs to the hospital the
    // user picks, so it's reported from the page they land on next.
    if (to.query.go !== undefined)
      return

    markSent(campaign)

    const providerUuid = typeof to.params.provider_uuid === 'string' ? to.params.provider_uuid : null

    send(campaign, providerUuid)
  })
}

/**
 * Forget which campaigns this tab has reported, on every login and logout.
 *
 * The "already sent" marks are per tab, not per person. Without this, at a shared
 * workstation the next person to sign in and open the same email link in that tab would
 * not be counted.
 */
export function resetCampaignArrivalsForAuthChange(): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i)
      if (key?.startsWith(STORAGE_PREFIX))
        keys.push(key)
    }
    keys.forEach(key => window.sessionStorage.removeItem(key))
  } catch {
    // Storage unavailable: nothing was recorded there either.
  }
}

/** Test seam: allow a re-install. */
export function __resetCampaignArrivalTrackingForTests(): void {
  installed = false
}
